import { v4 as uuidv4 } from 'uuid'
import getRandomValues from 'get-random-values'
import { computed } from 'vue'

export function dropRefs(o) {
  return JSON.parse(JSON.stringify(o))
}

export function getObjKey(obj, value) {
  return Object.keys(obj).find((key) => obj[key] === value)
}

export function computedInstance(props, instance) {
  const computedProps = Object.keys(props).reduce((a, i) => {
    a[i] = computed(() => props[i])
    return a
  }, {})

  for (const [key, value] of Object.entries(instance)) {
    computedProps[key] = computed(() => value)
  }

  return computedProps
}

/**
 * Returns the IE version number
 * @description
 */
export function isInternetExplorer(ua) {
  if (!ua) ua = window.navigator.userAgent
  return (
    ua.toUpperCase().indexOf('TRIDENT/') != -1 ||
    ua.toUpperCase().indexOf('MSIE') != -1
  )
}

/**
 * @param {Array} array
 * @param {Function} callback
 * @description
 */
export async function asyncForEach(array, callback) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array)
  }
}

/**
 * @param {Mixed} item
 * @returns {Boolean}
 * @description Checks if a given item is an object and returns
 * true if it is, else will return false.
 */
export const isObject = (item) =>
  typeof item === 'object' && !Array.isArray(item) && item !== null

export const isFalsy = (v) => !v

export const hasOwn = (obj, prop) => {
  return {}.hasOwnProperty.call(obj, prop)
}

export const camelToKebab = (string) => {
  return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

export const arrayify = (obj) => {
  return obj instanceof Array ? obj : [obj]
}

export const cloneObject = (obj) => {
  return Object.assign({}, obj)
}

export function defineDescriptor(src, dest, name) {
  if (!hasOwn(dest, name)) {
    const descriptor = Object.getOwnPropertyDescriptor(src, name)
    Object.defineProperty(dest, name, descriptor)
  }
}

export function buildFromProps(obj, props) {
  const res = {}
  props.forEach((prop) => defineDescriptor(obj, res, prop))
  return res
}

/**
 * @param {Array} objects
 * @param {Boolean} deep
 * @param {Array} exclude
 * @returns {Object}
 * @description Takes an array of objects to merge and returns a
 * new object with all the properties.
 */
export function mergeAll(objects, deep = false, exclude = []) {
  let res = {}
  for (const obj of objects) {
    if (isObject(obj)) {
      res = merge(res, obj, deep, exclude)
    }
  }
  return res
}

/**
 * @param {Object} target
 * @param {Object} source
 * @param {Boolean} deep
 * @param {Array} exclude
 * @returns {Object}
 * @description A simple method for merging objects together, if
 * merging nested object the whole nested object will be replaced
 * if it already exists on the target, unless the "deep" parameter
 * is set to true, then it will try to merge nested objects also.
 * If you have an "exclude" list "deep" will be set to true.
 */
export function merge(target, source, deep = false, exclude = []) {
  if (exclude.length > 0) deep = true
  if (deep || !Object.assign) {
    const isDeep = (prop) =>
      isObject(source[prop]) &&
      target !== null &&
      hasOwn(target, prop) &&
      isObject(target[prop])
    const replaced = Object.getOwnPropertyNames(source)
      .map((prop) => {
        if (isDeep(prop)) {
          return {
            [prop]: merge(target[prop], source[prop], deep),
          }
        }
        if (!exclude.includes(prop)) {
          return {
            [prop]: source[prop],
          }
        }
        return {}
      })
      .reduce((a, b) => ({ ...a, ...b }), {})
    return {
      ...target,
      ...replaced,
    }
  } else {
    return Object.assign(target, source)
  }
}

/**
 * @param {Object} obj
 * @param {String} path
 * @description Get value of an object property/path even if it's nested
 */
export function getValueByPath(obj, path) {
  if (path) {
    const value = path.split('.').reduce((o, i) => (o ? o[i] : null), obj)
    return value
  }
  return path
}

/**
 * @param {Array} array
 * @param {Object} obj
 * @param {Function} fn
 * @description Extension of indexOf method by equality function if specified
 */
export function indexOf(array, obj, fn) {
  if (!array) return -1

  if (!fn || typeof fn !== 'function') return array.indexOf(obj)

  for (let i = 0; i < array.length; i++) {
    if (fn(array[i], obj)) {
      return i
    }
  }

  return -1
}

export const truthyClassesToString = (obj, pre = '') => {
  const prefix = (o, p) => `${p}${o}`
  if (typeof obj === 'string') {
    return prefix(obj, pre)
  } else if (isObject(obj)) {
    return Object.keys(obj)
      .filter((i) => obj[i])
      .map((i) => prefix(i, pre))
      .join(' ')
  }
  return ''
}

export function removeElement(el) {
  if (typeof el.remove !== 'undefined') {
    el.remove()
  } else if (typeof el.parentNode !== 'undefined' && el.parentNode !== null) {
    el.parentNode.removeChild(el)
  }
}

/**
 * @param {String} value
 * @returns {String}
 * @description Escape regex characters
 * http://stackoverflow.com/a/6969486
 */
export function escapeRegExpChars(value) {
  if (!value) return value
  // eslint-disable-next-line
  return value.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&')
}

/**
 * @returns {String}
 * @description Creates a unique identifier.
 */
export function uuid() {
  const options = {}

  if (process.env.VUE_ENV !== 'browser') {
    const rnds8 = new Uint8Array(16)
    options.rng = () => getRandomValues(rnds8)
  }

  return uuidv4(options)
}

/**
 * @param {Number} ms
 * @returns {Promise}
 * @description Returns a promise to wait for a given number of
 * milliseconds
 */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * @param {Number} min
 * @param {Number} max
 * @returns {Number}
 * @description Returns a random number between the given range
 */
export function randomInteger(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

/**
 * @param {Array} data
 * @param {Array} stats
 * @param {Array} keys
 * @returns {Array}
 * @description Takes an array of akero data and stats, and will map the
 * correct stats onto each document.
 */
export function mapStats(data, stats, keys = []) {
  const getStats = (id) => stats.find((stat) => stat._id === id)
  const getStat = (key, stats) => (isObject(stats) ? stats[key] : 0)
  return data.map((item) => {
    const itemStats = getStats(item._id)
    for (const key of keys) {
      item[key] = getStat(key, itemStats)
    }
    return item
  })
}

/**
 * @param {Object} user
 * @description Returns the active account for the logged in user
 */
export function getActiveAccount(user) {
  const callback = (item) => item.account._id === user.account._id
  return user.accounts.find(callback)
}

/**
 * @param {Object} user
 * @returns {String}
 * @description Returns the users "userLevel" for the active account
 * that they are logged into
 */
export function getActiveUserLevel(user) {
  const account = getActiveAccount(user)
  return isObject(account) && typeof account.userLevel === 'string'
    ? account.userLevel
    : 'accountuser'
}

/**
 * @param {Array} arr
 * @param {Object} dragResult
 * @description Applies a drag reset to the array of content
 */
export function applyDrag(arr, dragResult) {
  const { removedIndex, addedIndex, payload } = dragResult
  if (removedIndex === null && addedIndex === null) return arr

  const result = [...arr]
  let itemToAdd = payload

  if (removedIndex !== null) {
    itemToAdd = result.splice(removedIndex, 1)[0]
  }

  if (addedIndex !== null) {
    result.splice(addedIndex, 0, itemToAdd)
  }

  return result
}

export function getTagProps(ctx, tagClasses) {
  const tag = ctx.$props.tag
  if (tag) {
    if (typeof tag === 'string') {
      const result = { value: tag }
      if (tagClasses) {
        result.props = { class: tagClasses }
      }
      return result
    } else if (typeof tag === 'object') {
      const result = { value: tag.value || 'div', props: tag.props || {} }

      if (tagClasses) {
        if (result.props.class) {
          if (Array.isArray(result.props.class)) {
            result.props.class.push(tagClasses)
          } else {
            result.props.class = [tagClasses, result.props.class]
          }
        } else {
          result.props.class = tagClasses
        }
      }

      return result
    }
  }
  return { value: 'div' }
}

export function validateTagProp(tag) {
  if (tag) {
    if (typeof tag === 'string') return true
    if (typeof tag === 'object') {
      if (
        typeof tag.value === 'string' ||
        typeof tag.value === 'function' ||
        typeof tag.value === 'object'
      ) {
        return true
      }
    }
    return false
  }
  return true
}

export function rgbaToString({ rgba }) {
  return ['rgba(', rgba.r, ',', rgba.g, ',', rgba.b, ',', rgba.a, ')'].join('')
}

export function debounce(func, wait, immediate) {
  var timeout
  return function () {
    const context = this
    const args = arguments

    var later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }

    const callNow = immediate && !timeout

    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function swapProtocol(url) {
  return url ? url.replace('http://', 'https://') : url
}

export function removeProtocol(str) {
  return str.replace(/(^\w+:|^)\/\//, '')
}

/**
 *
 * @param {String} content - The string content to write to clipboard
 * @returns {String}
 * @description Writes the given text to the clipboard
 */
export async function writeDataToClipboard(content) {
  return await navigator.clipboard.writeText(content)
}

/**
 * Turn a value to a slug
 * @param {String} value value to slugify
 */
export function slugger(value) {
  if (!value) return
  // Take a given string say '* test survey 1 @ foo (22/22/2334)'
  // and turn it into a slug test-survey-1-foo-22-22-2334
  let t = value.replace(/[\s\W_]/g, '-')
  // slug could contain multiple '-' e.g '--test-survey-1---foo--22-22-2334-'
  // So remove any instances of '-' when more than 1 exists
  // This gives us '-test-survey-1-foo-22-22-2334-'
  t = t.replace(/(-){2,}/g, '-')
  // It could start or end with a '-' so remove them
  // This now gives us test-survey-1-foo-22-22-2334
  t = t.replace(/^(-)|(-)$/g, '')
  return t.toLowerCase()
}

/**
 * @param {Object} obj
 * @param {Boolean=} part - if true starts with & not ?
 * @return {String}
 * @description Creates a query string from an object - currently does not
 * like nested properties or arrays.
 */
export function createQueryString(obj, part) {
  let query = ''

  if (isObject(obj)) {
    for (let key in obj) {
      if (hasOwn(obj, key)) {
        if (
          obj[key] !== '' &&
          obj[key] !== null &&
          typeof obj[key] !== 'undefined'
        ) {
          query += `&${key}=${obj[key]}`
        }
      }
    }

    query = (part ? '&' : '?') + query.substring(1)
  }

  return query
}

/**
 * @param {String} query
 * @returns {Object}
 * @description Parses the query params out of a string.
 */
export function parseQueryString(query) {
  const queryObject = {}

  query
    .split('?')
    .pop()
    .replace(new RegExp('([^?=&]+)(=([^&]*))?', 'g'), ($0, $1, $2, $3) => {
      queryObject[$1] = $3
    })

  return queryObject
}

/**
 * @param {String} url - The url for the cloudinary image
 * @param {Object} opts - The options for the cloudinary image
 * @param {*} type
 * @returns String
 * @description Returns a cloudinary url with the options applied to it
 */
export function cloudinaryOptions(url, opts, type) {
  let arr = url.split('/')
  let replacement = new Array()
  let test = new RegExp('^.[_]+', 'ig')
  let trail = '/'

  type = type + trail

  for (let opt in opts) {
    replacement.push(opt + '_' + opts[opt])
  }

  for (let key in arr) {
    const match = test.exec(arr[key])
    if (match) {
      arr[key] = replacement.join(',')
      return arr.join('/')
    }
  }

  const replace = type + replacement.join(',') + trail
  return url.replace(type, replace)
}

/**
 * @param {Function} callback - The function to execute on each interval.
 * @param {Number} delay - The number of milliseconds to delay each interval by. defaults to 1.
 * @param {Number} repetitions - The number of iterations to run. defaults to 100.
 * @returns {Function}
 * @description Sets an interval to run as long as the delay argument
 * specifies, until the max number of repetitions has been reached or
 * until the callback returns true. Returns a function to cancel the
 * interval.
 * @example
 *
 * let i = 0
 * setIntervalX(() => {
 *  if (++i === 10) {
 *    console.log('done: ' + i)
 *    return true
 *  } else {
 *    console.log('continue: ' + i)
 *  }
 * }, 10, 20)
 */
export function setIntervalX(callback, delay = 1, repetitions = 100) {
  let intID = null
  let x = 0

  intID = setInterval(() => {
    const isLast = ++x === repetitions
    const result = callback(isLast)
    if (result === true || isLast) {
      clearInterval(intID)
    }
  }, delay)

  return () => clearInterval(intID)
}
